import { Priority } from "@zero/types/enum";
import { cn } from "@zero/utils";
import { Plus, X } from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useState } from "react";
import { DEFAULT_CATEGORIES, useTodoStore } from "../store/todoStore";
import { fromDateInputValue } from "../utils/date";
import { parseTagInput } from "../utils/tags";

const PRIORITIES = [
  { value: Priority.Low, label: "低", color: "bg-success" },
  { value: Priority.Medium, label: "中", color: "bg-warning" },
  { value: Priority.High, label: "高", color: "bg-orange-500" },
  { value: Priority.Urgent, label: "紧急", color: "bg-destructive" },
] as const;

export function TodoForm() {
  const { isFormExpanded, setFormExpanded, addTodoAsync, isLoading, categories } = useTodoStore();
  const categoryNames = categories();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<Priority>(Priority.Medium);
  const [category, setCategory] = useState<string>(DEFAULT_CATEGORIES[0]);
  const [dueDate, setDueDate] = useState("");
  const [tags, setTags] = useState("");

  useEffect(() => {
    if (!categoryNames.includes(category)) {
      setCategory(categoryNames[0] ?? DEFAULT_CATEGORIES[0]);
    }
  }, [category, categoryNames]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || isLoading) {
      return;
    }

    try {
      await addTodoAsync({
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        dueDate: fromDateInputValue(dueDate),
        category,
        tags: parseTagInput(tags),
      });

      // Reset form
      setTitle("");
      setDescription("");
      setPriority(Priority.Medium);
      setCategory(categoryNames[0] ?? DEFAULT_CATEGORIES[0]);
      setDueDate("");
      setTags("");
      setFormExpanded(false);
    } catch (_error) {
      // 错误已在 API 层处理
    }
  };

  const handleQuickAdd = async () => {
    if (!title.trim()) {
      setFormExpanded(true);
      return;
    }

    try {
      await addTodoAsync({
        title: title.trim(),
        priority: Priority.Medium,
        category: categoryNames[0] ?? DEFAULT_CATEGORIES[0],
      });

      setTitle("");
    } catch (_error) {
      // 错误已在 API 层处理
    }
  };

  return (
    <div className="mb-6">
      {!isFormExpanded ? (
        <form onSubmit={handleQuickAdd} className="flex gap-2">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="添加新任务..."
            className="flex-1 h-9 px-3 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-ring focus:border-transparent text-foreground"
          />
          <button
            type="button"
            onClick={() => setFormExpanded(true)}
            className="px-3 py-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            展开
          </button>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 h-9 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium shadow-sm hover:bg-primary/90 transition-colors"
          >
            <Plus size={16} />
            添加
          </button>
        </form>
      ) : (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-card rounded-md shadow-sm border border-border overflow-hidden"
        >
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-foreground">创建新任务</h3>
              <button
                type="button"
                onClick={() => setFormExpanded(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Title */}
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="任务标题"
              className="w-full h-9 px-3 bg-muted border border-input rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-ring focus:border-transparent text-foreground"
            />

            {/* Description */}
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="任务描述（可选）"
              rows={2}
              className="w-full px-3 py-2 bg-muted border border-input rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-ring focus:border-transparent text-foreground resize-none"
            />

            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="标签，用逗号分隔"
              className="w-full h-9 px-3 bg-muted border border-input rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-ring focus:border-transparent text-foreground"
            />

            {/* Priority, Category & Due Date */}
            <div className="grid gap-4 md:grid-cols-[1fr_180px_180px]">
              <div>
                <span className="block text-sm font-medium text-foreground mb-2">优先级</span>
                <div className="flex gap-2">
                  {PRIORITIES.map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => setPriority(p.value)}
                      className={cn(
                        "flex-1 inline-flex items-center justify-center h-8 px-3 rounded-md text-sm font-medium transition-all",
                        priority === p.value
                          ? `${p.color} text-primary-foreground`
                          : "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                      )}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="todo-category" className="block text-sm font-medium text-foreground mb-2">
                  分类
                </label>
                <select
                  id="todo-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full h-9 px-3 bg-muted border border-input rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-ring focus:border-transparent text-foreground"
                  aria-label="任务分类"
                >
                  {categoryNames.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="todo-due-date" className="block text-sm font-medium text-foreground mb-2">
                  截止日期
                </label>
                <input
                  id="todo-due-date"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full h-9 px-3 bg-muted border border-input rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-ring focus:border-transparent text-foreground"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setFormExpanded(false)}
                className="px-4 py-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={!title.trim() || isLoading}
                className="inline-flex items-center justify-center gap-2 h-9 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium shadow-sm hover:bg-primary/90 disabled:bg-muted disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
              >
                <Plus size={16} />
                {isLoading ? "创建中..." : "创建任务"}
              </button>
            </div>
          </form>
        </motion.div>
      )}
    </div>
  );
}
